import { InfoRounded, PinRounded } from '@mui/icons-material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FormatAlignCenterRoundedIcon from '@mui/icons-material/FormatAlignCenterRounded'
import FormatAlignLeftRoundedIcon from '@mui/icons-material/FormatAlignLeftRounded'
import FormatAlignRightRoundedIcon from '@mui/icons-material/FormatAlignRightRounded'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import MoneyOffCsredRoundedIcon from '@mui/icons-material/MoneyOffCsredRounded'
import PercentRoundedIcon from '@mui/icons-material/PercentRounded'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import { Box, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { ITableCellStyle } from '../../../definitions/report_builder/ReportBuilderDefinitions'
import CustomIcon from '../../icons/CustomIcon'
import ColorPicker from './ColorPicker'

interface P {
    style?: ITableCellStyle
    onChange: (style: ITableCellStyle) => void
    label?: string
    formatNumbers?: boolean
    formatNumbersPercentage?: boolean
    formatNumbersFinancial?: boolean
    onFormatNumberChanges?: (formatNumber: boolean, roundDigits?: number) => void
    onFormatFinancialChanges?: (formatNumber: boolean, roundDigits?: number) => void
    onFormatPercentageChanges?: (formatNumber: boolean, roundDigits?: number) => void
    numberFormatter?: boolean
    onResetStyle?: () => void
    textAlign?: boolean
    headersStyle?: boolean
    textDecoration?: boolean
    roundDigits?: number
    onRoundDigitsChange?: (roundDigits: number) => void
    onNumberFormatReset?: () => void
}

const TextTableEditor = ({
    style, onChange, label, formatNumbers, formatNumbersPercentage, formatNumbersFinancial, numberFormatter, textAlign, headersStyle, textDecoration, roundDigits, onFormatNumberChanges, onFormatPercentageChanges, onFormatFinancialChanges, onRoundDigitsChange, onResetStyle, onNumberFormatReset
}: P) => {
    const iconSize = '30px'
    const [selectedFormat, setSelectedFormat] = useState(formatNumbers ? 'number' : formatNumbersPercentage ? 'percentage' : formatNumbersFinancial ? 'financial' : null)

    const handleTextColorChange = (color: string) => {
        if (color) {
            onChange({ ...style, color })
        } else {
            const { ...restStyle } = style
            onChange(restStyle)
        }
    }

    const handleBackgroundColorChange = (color: string) => {
        if (color) {
            onChange({ ...style, background: color })
        } else {
            const { ...restStyle } = style
            onChange(restStyle)
        }
    }

    return (
        <Box sx={{ mb: 2 }}>
            {label && (
                <Typography variant='caption' sx={{ minWidth: 20, mb: 1, display: 'block' }}>
                    {label}:
                </Typography>
            )}
            {textDecoration && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Tooltip title='Bold' placement='top'>
                        <IconButton
                            size='small'
                            onClick={() => onChange({ ...style, bold: !style.bold })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.bold ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.bold ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatBoldIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Strikethrough' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, strike: !style.strike })}
                            sx={{
                                'display': 'flex',
                                'alignItems': 'center',
                                'justifyContent': 'center',
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.strike ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.strike ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <StrikethroughSIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <ColorPicker
                        type='text'
                        color={style.color}
                        onColorChange={handleTextColorChange}
                        tooltip='Text Color'
                    />

                    <ColorPicker
                        type='background'
                        color={style.background}
                        onColorChange={handleBackgroundColorChange}
                        tooltip='Background Color'
                    />
                    <Tooltip title='Reset Text Style' placement='top'>
                        <IconButton
                            onClick={() => {
                                onChange({})
                                if (onResetStyle) onResetStyle()
                            }}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: 'action.hover'
                                }
                            }}>
                            <CloseRoundedIcon fontSize='medium' color='error' />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            {headersStyle && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Tooltip title='Text align left' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'left' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'left' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'left' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignLeftRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align center' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'center' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'center' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'center' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignCenterRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align right' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'right' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'right' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'right' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignRightRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Reset Text Style' placement='top'>
                        <IconButton
                            onClick={() => {
                                onChange({})
                                if (onFormatNumberChanges) onFormatNumberChanges(false)
                                if (onFormatPercentageChanges) onFormatPercentageChanges(false)
                                if (onResetStyle) onResetStyle()
                            }}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: 'action.hover'
                                }
                            }}>
                            <CloseRoundedIcon fontSize='medium' color='error' />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            {numberFormatter && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Tooltip title='Number Format' placement='top'>
                        <IconButton
                            onClick={() => {
                                onFormatNumberChanges(!formatNumbers, !formatNumbers ? 0 : null)
                                if (!formatNumbers) {
                                    setSelectedFormat('number')
                                } else {
                                    setSelectedFormat(null)
                                    onNumberFormatReset()
                                }
                            }}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': selectedFormat === 'number' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: selectedFormat === 'number' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <PinRounded fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Financial Format $$$' placement='top'>
                        <IconButton
                            onClick={() => {
                                onFormatFinancialChanges(!formatNumbersFinancial, !formatNumbersFinancial ? 0 : null)
                                if (!formatNumbersFinancial) {
                                    setSelectedFormat('financial')
                                } else {
                                    setSelectedFormat(null)
                                    onNumberFormatReset()
                                }
                            }}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': selectedFormat === 'financial' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: selectedFormat === 'financial' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <MoneyOffCsredRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Percentage Format' placement='top'>
                        <IconButton
                            onClick={() => {
                                onFormatPercentageChanges(!formatNumbersPercentage, !formatNumbersPercentage ? 0 : null)
                                if (!formatNumbersPercentage) {
                                    setSelectedFormat('percentage')
                                } else {
                                    setSelectedFormat(null)
                                    onNumberFormatReset()
                                }
                            }}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': selectedFormat === 'percentage' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: selectedFormat === 'percentage' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <PercentRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Reset Number Format' placement='top'>
                        <IconButton
                            onClick={() => {
                                onNumberFormatReset()
                                setSelectedFormat(null)
                            }}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: 'action.hover'
                                }
                            }}>
                            <CloseRoundedIcon fontSize='medium' color='error' />
                        </IconButton>
                    </Tooltip>
                    <Stack flexDirection='row' gap={1} alignItems='center'>
                        <TextField
                            size='small'
                            type='text'
                            inputMode='numeric'
                            name='roundDigits'
                            value={roundDigits ?? 0}
                            disabled={selectedFormat != null ? false : true}
                            inputProps={{ min: 0, step: 1, pattern: /[^0-9]/g }}
                            sx={{ maxWidth: '120px' }}
                            onChange={e => {
                                const val = e.target.value.replace(/[^0-9]/g, '')
                                onRoundDigitsChange(Number(val))
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <CustomIcon name='decimal' height='22.5' width='22.5' />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Tooltip arrow title='If a value format ($, %, number) is selected it is possible to select the round digits of the decimal part'>
                            <InfoRounded fontSize='inherit' />
                        </Tooltip>
                    </Stack>

                </Box>
            )}
            {textAlign && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Tooltip title='Text align left' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'left' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'left' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'left' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignLeftRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align center' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'center' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'center' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'center' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignCenterRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align right' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'right' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'right' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'right' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignRightRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

        </Box>
    )
}

export default TextTableEditor
